import {
  Alert,
  Button,
  Card,
  Group,
  Paper,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title as ChartTitle,
  Tooltip,
} from "chart.js";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import React from "react";
import { FaExclamationTriangle } from "react-icons/fa";
import { PerformancePeriod, WorkflowStatusKey } from "../../types/performance_analytics";

dayjs.locale("pt-br");

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Filler,
  ChartTitle,
  Tooltip,
  Legend
);

export const UNAVAILABLE_LABEL = "Indisponível";

export const STATUS_LABELS: Record<WorkflowStatusKey, string> = {
  draft: "Rascunho",
  in_review: "Em revisão",
  changes_requested: "Ajustes",
  approved: "Aprovado",
  scheduled: "Agendado",
  published: "Publicado",
};

export const STATUS_COLORS: Record<WorkflowStatusKey, string> = {
  draft: "#94a3b8",
  in_review: "#3b82f6",
  changes_requested: "#f97316",
  approved: "#22c55e",
  scheduled: "#8b5cf6",
  published: "#0f766e",
};

export const PERIOD_OPTIONS: { label: string; value: PerformancePeriod }[] = [
  { label: "Dia", value: "day" },
  { label: "Semana", value: "week" },
  { label: "Mês", value: "month" },
];

const pluralize = (value: number, singular: string, plural: string) => {
  return `${value} ${value === 1 ? singular : plural}`;
};

export const formatPercentage = (value?: number | null) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return UNAVAILABLE_LABEL;
  }

  return `${(value * 100).toLocaleString("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;
};

export const formatCount = (value?: number | null) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return UNAVAILABLE_LABEL;
  }

  return value.toLocaleString("pt-BR");
};

export const formatDate = (value?: string | null) => {
  if (!value) return UNAVAILABLE_LABEL;
  return dayjs(value).format("DD/MM/YYYY");
};

export const formatDateTime = (value?: string | null) => {
  if (!value) return UNAVAILABLE_LABEL;
  return dayjs(value).format("DD/MM/YYYY HH:mm");
};

export const formatDuration = (
  value?: number | null,
  mode: "compact" | "detailed" = "compact"
) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return UNAVAILABLE_LABEL;
  }

  const totalMinutes = Math.round(value / 60000);

  if (totalMinutes <= 0) {
    return mode === "compact" ? "0 min" : "0 minutos";
  }

  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;

  if (mode === "compact") {
    const pieces = [
      days > 0 ? `${days}d` : null,
      hours > 0 ? `${hours}h` : null,
      minutes > 0 ? `${minutes}min` : null,
    ].filter(Boolean);

    return pieces.slice(0, 2).join(" ");
  }

  const detailed = [
    days > 0 ? pluralize(days, "dia", "dias") : null,
    hours > 0 ? pluralize(hours, "hora", "horas") : null,
    minutes > 0 ? pluralize(minutes, "minuto", "minutos") : null,
  ].filter(Boolean);

  return detailed.join(", ");
};

export const formatBoolean = (value?: boolean | null) => {
  if (value === null || value === undefined) return UNAVAILABLE_LABEL;
  return value ? "Sim" : "Não";
};

export const getStatusLabel = (status?: string | null) => {
  if (!status) return "Sem status";
  if (status in STATUS_LABELS) {
    return STATUS_LABELS[status as WorkflowStatusKey];
  }
  return status;
};

export const getStatusColor = (status?: string | null) => {
  if (!status) return "gray";
  if (status in STATUS_COLORS) {
    return STATUS_COLORS[status as WorkflowStatusKey];
  }
  return "#64748b";
};

export const compareNullableNumbers = (left?: number | null, right?: number | null) => {
  const normalizedLeft = left ?? Number.NEGATIVE_INFINITY;
  const normalizedRight = right ?? Number.NEGATIVE_INFINITY;
  return normalizedLeft - normalizedRight;
};

export const EmptyAnalyticsState = ({
  title = "Sem analytics no período selecionado",
  description = "Ajuste os filtros para outro intervalo ou selecione outros clientes.",
}: {
  title?: string;
  description?: string;
}) => {
  return (
    <Paper withBorder p="xl">
      <Stack align="center" spacing="xs">
        <Title order={4}>{title}</Title>
        <Text c="dimmed" align="center">
          {description}
        </Text>
      </Stack>
    </Paper>
  );
};

export const ErrorAnalyticsState = ({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) => {
  return (
    <Alert
      icon={<FaExclamationTriangle size={16} />}
      color="red"
      title="Falha ao carregar analytics"
      variant="light"
    >
      <Stack spacing="sm">
        <Text size="sm">{message}</Text>
        <Group>
          <Button color="red" variant="light" onClick={onRetry}>
            Tentar novamente
          </Button>
        </Group>
      </Stack>
    </Alert>
  );
};

export const SectionPanel = ({
  title,
  description,
  right,
  children,
}: {
  title: string;
  description?: string;
  right?: React.ReactNode;
  children: React.ReactNode;
}) => {
  return (
    <Card withBorder>
      <Stack spacing="md">
        <Group position="apart" align="flex-start">
          <Stack spacing={2}>
            <Title order={4}>{title}</Title>
            {description ? (
              <Text size="sm" c="dimmed">
                {description}
              </Text>
            ) : null}
          </Stack>
          {right}
        </Group>
        {children}
      </Stack>
    </Card>
  );
};